import { useState } from 'react'
import { Link } from 'react-router-dom'
import AppPagination from '../../../components/AppPagination'
import { useTaskMasterListScreen } from './hooks/useTaskMasterListScreen'
import { listJobMasters } from '../job-master/api/jobMasterApi'
import { appToast } from '../../../components/AppToast'
import { appConfirm } from '../../../components/AppConfirmModal'
import MasterApprovalStatusBadge from '../../approval/components/MasterApprovalStatusBadge'

function priorityStyles(priority) {
  if (priority === 'HIGH') return { bg: 'bg-red-500/10 text-red-600 border border-red-500/20', dot: 'bg-red-500' }
  if (priority === 'MEDIUM') return { bg: 'bg-amber-500/10 text-amber-700 border border-amber-500/20', dot: 'bg-amber-500' }
  return { bg: 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/20', dot: 'bg-emerald-500' }
}

function slaColor(hours) {
  if (hours <= 8) return 'bg-emerald-500'
  if (hours <= 24) return 'bg-primary'
  if (hours <= 72) return 'bg-amber-500'
  return 'bg-error'
}

export default function TaskMasterListScreen() {
  const { state, actions } = useTaskMasterListScreen()

  const [expandedId, setExpandedId] = useState(null)
  const [jobsMap, setJobsMap] = useState({})
  const [jobsLoadingId, setJobsLoadingId] = useState(null)

  async function handleExpand(row) {
    if (expandedId === row.id) {
      setExpandedId(null)
      return
    }
    setExpandedId(row.id)
    if (jobsMap[row.id]) return
    setJobsLoadingId(row.id)
    try {
      const data = await listJobMasters({ taskMasterId: row.id, page_size: 100 })
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []
      setJobsMap((prev) => ({ ...prev, [row.id]: items }))
    } catch {
      appToast.error('Failed to load job checklist.')
    } finally {
      setJobsLoadingId(null)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-8">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Master Configuration</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-on-surface">Task Master</h1>
          <p className="mt-1 text-sm text-on-surface-variant">Standard blueprints — SLA timelines, effort estimates, and skill requirements.</p>
        </div>
        <Link
          to="/masters/task-master/entry"
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-on-primary shadow-md shadow-primary/25 transition hover:brightness-110"
        >
          <span className="material-symbols-outlined text-base">add</span>
          New Blueprint
        </Link>
      </header>

      {/* ── Toolbar ────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-2xl bg-surface-container-low px-5 py-3.5">
        <div className="flex items-center gap-3 flex-1">
          <span className="material-symbols-outlined text-lg text-on-surface-variant">search</span>
          <input
            type="text"
            value={state.searchText}
            onChange={(e) => actions.setSearchText(e.target.value)}
            placeholder="Search by name or code..."
            className="flex-1 border-none bg-transparent text-sm text-on-surface outline-none placeholder:text-on-surface-variant"
          />
        </div>

        <div className="flex items-center gap-3">
          <div className="flex rounded-xl bg-surface-container-high p-1 gap-1">
            {[
              { id: 'active', label: 'Active' },
              { id: 'pending', label: 'Pending Review' },
              { id: 'inactive', label: 'Inactive' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => actions.setStatusFilter(tab.id)}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
                  state.statusFilter === tab.id
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5 rounded-xl bg-primary/10 px-3 py-1.5">
            <span className="material-symbols-outlined text-sm text-primary">assignment</span>
            <span className="text-sm font-extrabold text-primary">{state.totalCount || state.rows.length}</span>
          </div>
        </div>
      </div>

      {/* ── Alerts ─────────────────────────────────────────────────────── */}
      {state.error ? (
        <div className="rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}
      {state.success ? (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{state.success}</div>
      ) : null}

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest overflow-hidden shadow-sm">

        {/* Table Header */}
        <div className="grid grid-cols-[2fr_1fr_100px_90px_90px_1fr_100px] gap-4 border-b border-outline-variant/15 bg-surface-container-low/60 px-5 py-3">
          {['Name / Code', 'Skills', 'Priority', 'SLA', 'Effort', 'Status', 'Actions'].map((col) => (
            <span key={col} className="text-[10px] font-black uppercase tracking-wider text-on-surface-variant">
              {col}
            </span>
          ))}
        </div>

        {/* Rows */}
        {state.isLoading ? (
          <div className="px-6 py-16 text-center text-sm text-on-surface-variant">Loading blueprints...</div>
        ) : state.rows.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-on-surface-variant">
            No task blueprints found. Create your first one.
          </div>
        ) : (
          state.rows.map((row) => {
            const isExpanded = expandedId === row.id
            const jobs = jobsMap[row.id] || []
            const isLoadingJobs = jobsLoadingId === row.id
            const pri = priorityStyles(row.priority)
            const slaWidth = Math.min(100, (row.sla_hours / 120) * 100)
            const skills = row.skill_tags ? row.skill_tags.split(',').map((t) => t.trim()).filter(Boolean) : []

            return (
              <div key={row.id} className="border-b border-outline-variant/10 last:border-b-0">
                {/* Main Row */}
                <div
                  className={`grid grid-cols-[2fr_1fr_100px_90px_90px_1fr_100px] gap-4 px-5 py-4 transition duration-150 cursor-pointer ${
                    isExpanded ? 'bg-primary/5' : 'hover:bg-surface-container-low/60'
                  }`}
                  onClick={() => handleExpand(row)}
                >
                  {/* Name / Code */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`flex-none w-1.5 h-1.5 rounded-full ${pri.dot}`}
                      />
                      <p className="truncate text-sm font-bold text-on-surface">{row.name}</p>
                    </div>
                    <p className="mt-0.5 text-[10px] font-mono uppercase tracking-wider text-on-surface-variant">{row.code}</p>
                  </div>

                  {/* Skills */}
                  <div className="flex flex-wrap gap-1 items-start">
                    {skills.length > 0 ? (
                      <>
                        {skills.slice(0, 2).map((tag) => (
                          <span key={tag} className="inline-block rounded-md bg-surface-container-high px-1.5 py-0.5 text-[9px] font-medium text-on-surface-variant">
                            {tag}
                          </span>
                        ))}
                        {skills.length > 2 && (
                          <span className="inline-block rounded-md bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold text-primary">
                            +{skills.length - 2}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-[10px] text-on-surface-variant/50 italic">—</span>
                    )}
                  </div>

                  {/* Priority */}
                  <div>
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wider ${pri.bg}`}>
                      {row.priority}
                    </span>
                  </div>

                  {/* SLA */}
                  <div className="pr-2">
                    <p className="text-xs font-bold text-on-surface">{row.sla_hours}h</p>
                    <div className="mt-1 h-1 w-full rounded-full bg-surface-container-high overflow-hidden">
                      <div
                        className={`h-full rounded-full ${slaColor(row.sla_hours)}`}
                        style={{ width: `${slaWidth}%` }}
                      />
                    </div>
                  </div>

                  {/* Effort */}
                  <div>
                    <p className="text-xs font-bold text-on-surface">{row.default_estimated_hours?.toFixed(1)}h</p>
                    <p className="text-[10px] text-on-surface-variant">estimated</p>
                  </div>

                  {/* Status */}
                  <div className="flex items-center">
                    <MasterApprovalStatusBadge
                      status={row.status === 'Active' ? 'APPROVED' : (row.status === 'Pending Review' ? 'PENDING' : row.status)}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Link
                      to={`/masters/task-master/entry?id=${row.id}`}
                      className="rounded-lg p-1.5 text-on-surface-variant hover:bg-surface-container-high hover:text-primary transition"
                      title="Edit"
                    >
                      <span className="material-symbols-outlined text-[16px]">edit</span>
                    </Link>
                    <button
                      type="button"
                      disabled={state.isMutating}
                      onClick={async () => {
                        const confirmed = await appConfirm({
                          title: row.isArchived ? 'Delete Task Blueprint' : 'Archive Task Blueprint',
                          message: row.isArchived
                            ? `Are you sure you want to permanently delete "${row.title}"?`
                            : `Archive "${row.title}"?`,
                          confirmText: row.isArchived ? 'Delete' : 'Archive',
                          variant: 'danger',
                        })
                        if (confirmed) actions.handleRowDeleteAction(row)
                      }}
                      className="rounded-lg p-1.5 text-on-surface-variant hover:bg-error-container hover:text-error transition disabled:opacity-50"
                      title={row.isArchived ? 'Delete' : 'Archive'}
                    >
                      <span className="material-symbols-outlined text-[16px]">delete</span>
                    </button>
                    <span
                      className={`material-symbols-outlined text-[16px] text-on-surface-variant transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                    >
                      expand_more
                    </span>
                  </div>
                </div>

                {/* Inline Expanded Checklist */}
                {isExpanded && (
                  <div className="border-t border-primary/10 bg-primary/[0.03] px-8 py-5">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="material-symbols-outlined text-base text-primary">checklist</span>
                      <p className="text-xs font-black uppercase tracking-wider text-primary">
                        Job Checklist Sequence — {row.name}
                      </p>
                    </div>

                    {isLoadingJobs ? (
                      <p className="text-xs text-on-surface-variant py-3">Loading checklist items...</p>
                    ) : jobs.length === 0 ? (
                      <p className="text-xs text-on-surface-variant/60 italic py-3">
                        No job items configured under this blueprint.{' '}
                        <Link to="/masters/job-master/entry" className="text-primary underline font-semibold">
                          Add a job item
                        </Link>
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-3">
                        {jobs.map((job, idx) => (
                          <div key={job.id} className="flex items-start gap-2.5 rounded-xl border border-outline-variant/15 bg-surface-container-lowest p-3 shadow-xs min-w-[180px] max-w-[220px]">
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary text-[10px] font-black text-on-primary">
                              #{idx + 1}
                            </span>
                            <div className="min-w-0">
                              <p className="truncate text-xs font-bold text-on-surface">{job.job_type_name || job.name}</p>
                              <p className="text-[9px] font-mono text-on-surface-variant">{job.job_type_code || job.code}</p>
                              <div className="mt-1 flex gap-2 text-[9px] text-on-surface-variant">
                                <span>{job.default_estimated_hours?.toFixed(1) || '0'}h est</span>
                                {job.sla_hours ? (
                                  <span className="text-amber-600 font-semibold">SLA: {job.sla_hours}h</span>
                                ) : (
                                  <span className="opacity-60">SLA: inherited</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* ── Pagination ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest px-6 py-4 sm:flex-row sm:items-center sm:justify-between shadow-sm">
        <span className="text-sm text-on-surface-variant">
          Showing <strong>{state.rangeStart}–{state.rangeEnd}</strong> of <strong>{state.totalCount || state.rows.length}</strong> blueprints
        </span>
        <AppPagination
          page={state.page}
          totalPages={state.totalPages}
          onPageChange={actions.goToPage}
          onPrevious={actions.goToPreviousPage}
          onNext={actions.goToNextPage}
          canPrevious={state.page > 1}
          canNext={Boolean(state.hasMore) || state.page < state.totalPages}
        />
      </div>
    </div>
  )
}
